import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { firestore } from '../../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { uploadUserFile } from '../../firebase/storageHelpers';

import { loadProfileImage } from '../../utils/profileImageUtils';

const TABS = [
  { key: 'company', label: 'Personal Information' },
];

const ProfileDashboard = () => {
  const { currentUser } = useAuth();
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('company');
  const [profileImageUrl, setProfileImageUrl] = useState('');
  const [editProfile, setEditProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    location: '',
    aboutMe: '',
    jobTitle: '',
    expectedRate: '',
    workAvailability: '',
    workPeriod: '',
    startDate: '',
    skills: [],
    languages: [],
    desiredJobs: [],
    educations: [],
    experiences: [],
    photoURL: '',
  });
  const [profileImage, setProfileImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef();
  
  // Education and Experience modal states
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [educationForm, setEducationForm] = useState({
    degree: '',
    major: '',
    school: '',
    year: '',
  });
  const [experienceForm, setExperienceForm] = useState({
    jobTitle: '',
    businessName: '',
    description: '',
    startDate: '',
    endDate: '',
    isCurrentWork: false,
    skills: [],
  });
  const [currentSkill, setCurrentSkill] = useState('');
  
  // Auto-complete suggestions
  const [schoolSuggestions, setSchoolSuggestions] = useState([]);
  const [majorSuggestions, setMajorSuggestions] = useState([]);
  const [showSchoolSuggestions, setShowSchoolSuggestions] = useState(false);
  const [showMajorSuggestions, setShowMajorSuggestions] = useState(false);
  const [schoolSearchTimeout, setSchoolSearchTimeout] = useState(null);

  // API-based university search
  const searchUniversities = async (query) => {
    if (query.length < 2) return [];
    
    try {
      const response = await fetch(`https://universities.hipolabs.com/search?name=${encodeURIComponent(query)}&limit=5`);
      const universities = await response.json();
      return universities.map(uni => uni.name);
    } catch (error) {
      console.error('Error fetching universities:', error);
      // Fallback to local data if API fails
      return commonSchoolsFallback.filter(school =>
        school.toLowerCase().includes(query.toLowerCase())
      ).slice(0, 5);
    }
  };

  // Fallback data in case API is down
  const commonSchoolsFallback = [
    'University of California', 'Harvard University', 'Stanford University', 'MIT', 'Yale University',
    'Princeton University', 'Columbia University', 'University of Pennsylvania', 'Cornell University',
    'Dartmouth College', 'Brown University', 'University of Chicago', 'Northwestern University',
    'Duke University', 'Vanderbilt University', 'Rice University', 'Notre Dame', 'Georgetown University',
    'Carnegie Mellon University', 'University of Southern California', 'UCLA', 'UC Berkeley',
    'University of Michigan', 'University of Virginia', 'University of North Carolina', 'Georgia Tech',
    'University of Texas', 'Arizona State University', 'University of Florida', 'Ohio State University',
    'Penn State University', 'University of Washington', 'University of Wisconsin', 'Indiana University',
    'Purdue University', 'University of Illinois', 'University of Minnesota', 'University of Iowa'
  ];

  // Common high school patterns and well-known schools
  const commonHighSchools = [
    // Public school patterns
    'Lincoln High School', 'Washington High School', 'Roosevelt High School', 'Jefferson High School',
    'Madison High School', 'Jackson High School', 'Central High School', 'North High School',
    'South High School', 'East High School', 'West High School', 'Kennedy High School',
    'Wilson High School', 'Franklin High School', 'Adams High School', 'Hamilton High School',
    
    // Well-known private/prep schools
    'Phillips Exeter Academy', 'Phillips Andover Academy', 'Groton School', 'Choate Rosemary Hall',
    'Deerfield Academy', 'Lawrenceville School', 'St. Paul\'s School', 'Hotchkiss School',
    'Milton Academy', 'Middlesex School', 'Concord Academy', 'Emma Willard School',
    
    // Religious schools
    'St. Mary\'s High School', 'St. Joseph\'s High School', 'Holy Cross High School',
    'Sacred Heart High School', 'St. Patrick\'s High School', 'Bishop O\'Connell High School',
    
    // Regional patterns
    'Brooklyn Technical High School', 'Stuyvesant High School', 'Bronx High School of Science',
    'Thomas Jefferson High School for Science and Technology', 'Montgomery Blair High School',
    'Walt Whitman High School', 'Bethesda-Chevy Chase High School'
  ];

  const commonMajors = [
    'Computer Science', 'Business Administration', 'Psychology', 'Biology', 'Engineering',
    'English', 'History', 'Political Science', 'Economics', 'Marketing', 'Finance', 'Accounting',
    'Management', 'Communications', 'Sociology', 'Chemistry', 'Physics', 'Mathematics',
    'Criminal Justice', 'Education', 'Nursing', 'Art', 'Music', 'Philosophy', 'International Relations',
    'Environmental Science', 'Public Health', 'Social Work', 'Journalism', 'Theatre', 'Film Studies',
    'Anthropology', 'Geography', 'Religion', 'Foreign Languages', 'Hospitality Management',
    'Information Technology', 'Data Science', 'Mechanical Engineering', 'Electrical Engineering',
    'Civil Engineering', 'Chemical Engineering', 'Biomedical Engineering', 'Architecture',
    'Graphic Design', 'Interior Design', 'Fashion Design', 'Culinary Arts', 'Sports Management'
  ];

  // High school tracks/programs
  const highSchoolPrograms = [
    'General Studies', 'College Preparatory', 'Advanced Placement (AP)', 'International Baccalaureate (IB)',
    'STEM Track', 'Science and Technology', 'Pre-Engineering', 'Pre-Medical',
    'Business and Finance', 'Arts and Humanities', 'Visual Arts', 'Performing Arts',
    'Music Program', 'Theatre Arts', 'Creative Writing', 'Journalism',
    'Athletics', 'Health Sciences', 'Criminal Justice', 'Culinary Arts',
    'Automotive Technology', 'Information Technology', 'Computer Programming',
    'Early Childhood Education', 'Agricultural Studies', 'Environmental Science'
  ];

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!currentUser) return;
      try {
        const profileDoc = await getDoc(doc(firestore, 'userProfiles', currentUser.uid));
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setUserProfile(data);
          setEditProfile({
            firstName: data.firstName || '',
            lastName: data.lastName || '',
            email: currentUser.email || '',
            phoneNumber: data.phoneNumber || '',
            location: data.location || '',
            aboutMe: data.aboutMe || '',
            jobTitle: data.jobTitle || '',
            expectedRate: data.expectedRate || '',
            workAvailability: data.workAvailability || '',
            workPeriod: data.workPeriod || '',
            startDate: data.startDate || '',
            skills: data.skills || [],
            languages: data.languages || [],
            desiredJobs: data.desiredJobs || [],
            educations: data.educations || [],
            experiences: data.experiences || [],
            photoURL: data.profileImagePath || data.photoURL || '',
          });
          const imageUrl = await loadProfileImage(data);
          if (imageUrl) {
            setProfileImageUrl(imageUrl);
            setEditProfile(prev => ({ ...prev, photoURL: imageUrl }));
          }
        } else {
          setEditProfile({
            firstName: '',
            lastName: '',
            email: currentUser.email || '',
            phoneNumber: '',
            location: '',
            aboutMe: '',
            jobTitle: '',
            expectedRate: '',
            workAvailability: '',
            workPeriod: '',
            startDate: '',
            skills: [],
            languages: [],
            desiredJobs: [],
            educations: [],
            experiences: [],
            photoURL: '',
          });
        }
      } catch (err) {
        setError('Failed to load profile.');
      } finally {
        setLoading(false);
      }
    };
    fetchUserProfile();
  }, [currentUser]);





  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditProfile((prev) => ({ ...prev, [name]: value }));
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (phoneNumber.length >= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    } else if (phoneNumber.length >= 3) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return phoneNumber;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setEditProfile((prev) => ({ ...prev, phoneNumber: formattedPhone }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setProfileImage(e.target.files[0]);
      const objectURL = URL.createObjectURL(e.target.files[0]);
      setProfileImageUrl(objectURL);
      setEditProfile((prev) => ({ ...prev, photoURL: objectURL }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let photoURL = editProfile.photoURL;
      // If a new image is selected, upload it
      if (profileImage) {
        try {
          photoURL = await uploadUserFile('userProfilePhoto', currentUser.uid, profileImage, 'profile');
        } catch (uploadErr) {
          setError('Failed to upload profile image.');
          setSaving(false);
          return;
        }
      }
      // Save profile data to Firestore, including the new photoURL if uploaded
      await setDoc(doc(firestore, 'userProfiles', currentUser.uid), {
        ...userProfile,
        ...editProfile,
        photoURL,
        profileImagePath: photoURL,
      }, { merge: true });
      setEditProfile((prev) => ({ ...prev, photoURL }));
      setProfileImageUrl(photoURL);
      setSuccess('Profile updated successfully!');
      setProfileImage(null); // Reset after upload
    } catch (err) {
      setError('Failed to save profile.');
    } finally {
      setSaving(false);
    }
  };



  // Helper function to check if a field is missing
  const isFieldMissing = (fieldName) => {
    const value = editProfile[fieldName];
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === '' || value === null || value === undefined;
  };

  // Helper function to get input className with missing field styling
  const getInputClassName = (fieldName, baseClassName = '') => {
    const baseClass = baseClassName || "w-full border rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent";
    const missing = isFieldMissing(fieldName);
    
    if (missing) {
      return `${baseClass} border-red-500 bg-red-50 focus:ring-red-500 animate-shake`;
    } else {
      return `${baseClass} border-gray-300 focus:ring-[#FF8C00]`;
    }
  };

  // Helper function to get label className with missing field styling  
  const getLabelClassName = (fieldName) => {
    const missing = isFieldMissing(fieldName);
    return missing 
      ? "block text-xs sm:text-sm font-medium mb-1 text-left text-red-600" 
      : "block text-xs sm:text-sm font-medium mb-1 text-left";
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.suggestion-container')) {
        setShowSchoolSuggestions(false);
        setShowMajorSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Education handlers
  const handleEducationInputChange = (e) => {
    const { name, value } = e.target;
    setEducationForm(prev => ({ ...prev, [name]: value }));
    
    // Handle auto-complete suggestions
    if (name === 'school') {
      // Clear previous timeout
      if (schoolSearchTimeout) {
        clearTimeout(schoolSearchTimeout);
      }
      
      if (value.length > 1) {
        // Check if it's high school or university
        const isHighSchool = educationForm.degree === 'High School';
        
        if (isHighSchool) {
          // Search high schools locally
          const filteredSchools = commonHighSchools.filter(school =>
            school.toLowerCase().includes(value.toLowerCase())
          ).slice(0, 5);
          setSchoolSuggestions(filteredSchools);
          setShowSchoolSuggestions(filteredSchools.length > 0);
        } else {
          // Debounce API calls for universities
          const timeout = setTimeout(async () => {
            const filteredSchools = await searchUniversities(value);
            setSchoolSuggestions(filteredSchools);
            setShowSchoolSuggestions(filteredSchools.length > 0);
          }, 300);
          setSchoolSearchTimeout(timeout);
        }
      } else {
        setShowSchoolSuggestions(false);
      }
    }
    
    // Clear suggestions when degree changes
    if (name === 'degree') {
      setShowSchoolSuggestions(false);
      setSchoolSuggestions([]);
      setShowMajorSuggestions(false);
      setMajorSuggestions([]);
    }
    
    if (name === 'major' && value.length > 0) {
      // Choose data source based on degree type
      const isHighSchool = educationForm.degree === 'High School';
      const dataSource = isHighSchool ? highSchoolPrograms : commonMajors;
      
      const filteredMajors = dataSource.filter(major =>
        major.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setMajorSuggestions(filteredMajors);
      setShowMajorSuggestions(filteredMajors.length > 0);
    } else if (name === 'major') {
      setShowMajorSuggestions(false);
    }
  };
  
  const handleSchoolSuggestionClick = (school) => {
    setEducationForm(prev => ({ ...prev, school }));
    setShowSchoolSuggestions(false);
  };
  
  const handleMajorSuggestionClick = (major) => {
    setEducationForm(prev => ({ ...prev, major }));
    setShowMajorSuggestions(false);
  };

  const handleAddEducation = () => {
    if (educationForm.degree && educationForm.school && educationForm.year) {
      const newEducation = {
        ...educationForm,
        id: Date.now().toString(), // Simple ID generation
      };
      setEditProfile(prev => ({
        ...prev,
        educations: [...prev.educations, newEducation]
      }));
      setEducationForm({ degree: '', major: '', school: '', year: '' });
      setShowEducationModal(false);
    }
  };

  // Experience handlers
  const handleExperienceInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExperienceForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAddSkillToExperience = () => {
    if (currentSkill.trim() && !experienceForm.skills.includes(currentSkill.trim())) {
      setExperienceForm(prev => ({
        ...prev,
        skills: [...prev.skills, currentSkill.trim()]
      }));
      setCurrentSkill('');
    }
  };

  const handleRemoveSkillFromExperience = (skillToRemove) => {
    setExperienceForm(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleAddExperience = () => {
    if (experienceForm.jobTitle && experienceForm.businessName && experienceForm.startDate) {
      const newExperience = {
        ...experienceForm,
        id: Date.now().toString(),
        startDate: { __time__: new Date(experienceForm.startDate).toISOString() },
        endDate: experienceForm.endDate ? { __time__: new Date(experienceForm.endDate).toISOString() } : null,
      };
      setEditProfile(prev => ({
        ...prev,
        experiences: [...prev.experiences, newExperience]
      }));
      setExperienceForm({
        jobTitle: '',
        businessName: '',
        description: '',
        startDate: '',
        endDate: '',
        isCurrentWork: false,
        skills: [],
      });
      setShowExperienceModal(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 sm:p-6 flex justify-center items-center h-[50vh] sm:h-[70vh]">
        <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-t-2 border-b-2 border-[#FF8C00]"></div>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto">
      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-2px); }
          20%, 40%, 60%, 80% { transform: translateX(2px); }
        }
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-1 text-left">Your Profile</h1>
        <p className="text-sm sm:text-base text-gray-500 text-left">Manage your personal information and job preferences</p>
      </div>
      
      {/* Tabs - Responsive with scrolling on mobile */}
      <div className="mb-4 sm:mb-6 overflow-x-auto">
        <div className="flex gap-1 sm:gap-2 min-w-max sm:min-w-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`px-3 sm:px-6 py-2 rounded-t-md font-medium border-b-2 transition-colors whitespace-nowrap text-sm sm:text-base ${
                activeTab === tab.key 
                  ? 'border-[#FF8C00] bg-white text-[#FF8C00]' 
                  : 'border-transparent bg-gray-100 text-gray-600 hover:text-[#FF8C00]'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Tab Content */}
      {activeTab === 'company' && (
        <form className="bg-white rounded-lg shadow p-3 sm:p-6" onSubmit={handleSave}>
          <div className="mb-4 sm:mb-6">
            <h2 className="text-lg sm:text-xl font-bold mb-1 text-left">Personal Information</h2>
            <p className="text-sm sm:text-base text-gray-500 text-left">Complete your profile to help employers find you and understand your qualifications.</p>
            
            {/* Profile Completion Progress */}
            {(() => {
              // Define essential fields for profile completion with display names
              const essentialFieldsConfig = {
                'firstName': 'First Name',
                'lastName': 'Last Name', 
                'phoneNumber': 'Phone Number',
                'location': 'Location',
                'jobTitle': 'Job Title',
                'expectedRate': 'Expected Hourly Rate',
                'workAvailability': 'Work Availability',
                'workPeriod': 'Preferred Schedule',
                'startDate': 'Available Start Date',
                'skills': 'Skills',
                'languages': 'Languages', 
                'desiredJobs': 'Desired Job Positions',
                'aboutMe': 'About Me',
                'educations': 'Education',
                'experiences': 'Work Experience'
              };
              
              const essentialFields = Object.keys(essentialFieldsConfig);
              
              // Calculate completion based on essential fields only
              const completedFields = essentialFields.filter(field => {
                const value = editProfile[field];
                if (Array.isArray(value)) {
                  return value.length > 0;
                }
                return value !== '' && value !== null && value !== undefined;
              });
              
              const missingFields = essentialFields.filter(field => {
                const value = editProfile[field];
                if (Array.isArray(value)) {
                  return value.length === 0;
                }
                return value === '' || value === null || value === undefined;
              });
              
              const completionPercentage = Math.round((completedFields.length / essentialFields.length) * 100);
              
              // Store missing fields in a way components can access
              window.missingProfileFields = missingFields;
              
              // Only show progress bar if not 100% complete
              return completionPercentage < 100 ? (
                <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">Profile Completion</span>
                    <span className="text-sm text-blue-700">{completionPercentage}%</span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                      style={{ width: `${completionPercentage}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-700 mt-1">
                    Complete your profile to increase your chances of being discovered by employers!<br/>
                    <span className="font-medium">
                      Missing: {missingFields.map(field => essentialFieldsConfig[field]).join(', ')}
                    </span>
                  </p>
                </div>
              ) : null;
            })()}
          </div>
          
          {/* Profile Image Section - Responsive */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center mb-4 sm:mb-6 gap-3 sm:gap-4">
            <div className="flex flex-col items-center gap-2">
              <div className="w-16 h-16 sm:w-20 sm:h-20">
                <img
                  src={
                    profileImageUrl || 
                    (editProfile.photoURL 
                      ? (editProfile.photoURL.startsWith('http') 
                          ? editProfile.photoURL 
                          : `/asset/${editProfile.photoURL}`)
                      : '/asset/default-avatar.png')
                  }
                  alt="Profile"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border"
                  onError={(e) => {
                    e.target.src = '/asset/default-avatar.png';
                  }}
                />
              </div>
              <button
                type="button"
                className="text-xs sm:text-sm text-[#FF8C00] hover:text-[#FF7700] font-medium transition-colors bg-white border border-[#FF8C00] hover:border-[#FF7700] px-3 py-1 rounded-md hover:bg-orange-50"
                onClick={() => fileInputRef.current.click()}
              >
                Change Photo
              </button>
              <input
                type="file"
                accept="image/*"
                className="hidden"
                ref={fileInputRef}
                onChange={handleImageChange}
              />
            </div>
            <div className="text-center sm:text-left">
              <div className="font-bold text-base sm:text-lg">
                {editProfile.firstName || 'First name'} {editProfile.lastName || 'Last name'}
              </div>
              <div className="text-gray-500 text-xs sm:text-sm">
                Your basic information visible to employers
              </div>
            </div>
          </div>
          
          {/* Job Information Section */}
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
              <svg className="h-5 w-5 mr-2 text-[#FF8C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Contact & Job Information
            </h3>
          
          {/* Form Fields - Responsive Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
            <div>
              <label className={getLabelClassName('firstName')}>First name</label>
              <input 
                type="text" 
                name="firstName" 
                value={editProfile.firstName} 
                onChange={handleInputChange} 
                className={getInputClassName('firstName')} 
                placeholder="Enter your first name"
              />
            </div>
            <div>
              <label className={getLabelClassName('lastName')}>Last name</label>
              <input 
                type="text" 
                name="lastName" 
                value={editProfile.lastName} 
                onChange={handleInputChange} 
                className={getInputClassName('lastName')} 
                placeholder="Enter your last name"
              />
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-1 text-left">Email</label>
              <input 
                type="email" 
                name="email" 
                value={editProfile.email} 
                disabled 
                className="w-full border border-gray-200 rounded-md px-3 py-2 bg-gray-100 cursor-not-allowed text-sm sm:text-base text-gray-600" 
              />
            </div>
            <div>
              <label className={getLabelClassName('phoneNumber')}>Phone Number</label>
              <input 
                type="tel" 
                name="phoneNumber" 
                value={editProfile.phoneNumber} 
                onChange={handlePhoneChange} 
                className={getInputClassName('phoneNumber')} 
                placeholder="(555) 123-4567"
                pattern="\([0-9]{3}\) [0-9]{3}-[0-9]{4}"
              />
            </div>
            <div>
              <label className={getLabelClassName('location')}>Location</label>
              <div className="relative">
                <input 
                  type="text" 
                  name="location" 
                  value={editProfile.location} 
                  onChange={handleInputChange} 
                  className={getInputClassName('location', "w-full border rounded-md px-3 py-2 pr-10 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")} 
                  placeholder="Enter your address or city"
                  list="locations-datalist"
                />
                <svg className="absolute right-3 top-3 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <datalist id="locations-datalist">
                  <option value="New York, NY" />
                  <option value="Los Angeles, CA" />
                  <option value="Chicago, IL" />
                  <option value="Houston, TX" />
                  <option value="Phoenix, AZ" />
                  <option value="Philadelphia, PA" />
                  <option value="San Antonio, TX" />
                  <option value="San Diego, CA" />
                  <option value="Dallas, TX" />
                  <option value="San Jose, CA" />
                </datalist>
              </div>
            </div>
            <div>
              <label className={getLabelClassName('jobTitle')}>Job Title</label>
              <div className="relative">
                <input 
                  type="text" 
                  name="jobTitle" 
                  value={editProfile.jobTitle} 
                  onChange={handleInputChange} 
                  className={getInputClassName('jobTitle')} 
                  placeholder="e.g., Server, Cashier, Manager"
                  list="job-titles-datalist"
                />
                <datalist id="job-titles-datalist">
                  <option value="Server" />
                  <option value="Host/Hostess" />
                  <option value="Cashier" />
                  <option value="Cook" />
                  <option value="Bartender" />
                  <option value="Manager" />
                  <option value="Assistant Manager" />
                  <option value="Busser" />
                  <option value="Kitchen Helper" />
                  <option value="Delivery Driver" />
                </datalist>
              </div>
            </div>
            <div>
              <label className={getLabelClassName('expectedRate')}>Expected Hourly Rate</label>
              <div className="relative">
                <span className="absolute left-3 top-3 text-gray-500">$</span>
                <input 
                  type="number" 
                  name="expectedRate" 
                  value={editProfile.expectedRate.replace('$', '').replace('/hour', '')} 
                  onChange={(e) => {
                    const value = e.target.value;
                    setEditProfile(prev => ({ ...prev, expectedRate: value ? `$${value}/hour` : '' }));
                  }}
                  className={getInputClassName('expectedRate', "w-full border rounded-md pl-8 pr-16 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")} 
                  placeholder="15"
                  min="7.25"
                  step="0.25"
                />
                <span className="absolute right-3 top-3 text-gray-500">/hour</span>
              </div>
            </div>
            <div>
              <label className={getLabelClassName('workAvailability')}>Work Availability</label>
              <select 
                name="workAvailability" 
                value={editProfile.workAvailability} 
                onChange={handleInputChange} 
                className={getInputClassName('workAvailability')}
              >
                <option value="">Select availability...</option>
                <option value="Full-time">Full-time (40+ hours/week)</option>
                <option value="Part-time">Part-time (20-39 hours/week)</option>
                <option value="Contract">Contract (Project-based)</option>
                <option value="Freelance">Freelance (As needed)</option>
                <option value="Temporary">Temporary (Short-term)</option>
                <option value="Seasonal">Seasonal</option>
              </select>
            </div>
            <div>
              <label className={getLabelClassName('workPeriod')}>Preferred Schedule</label>
              <div className={`grid grid-cols-2 gap-2 ${isFieldMissing('workPeriod') ? 'animate-shake' : ''}`}>
                {['Morning', 'Afternoon', 'Evening', 'Night', 'Weekdays', 'Weekends'].map((schedule) => (
                  <label key={schedule} className={`flex items-center space-x-2 text-sm ${isFieldMissing('workPeriod') ? 'text-red-600' : ''}`}>
                    <input
                      type="checkbox"
                      checked={editProfile.workPeriod.includes(schedule)}
                      onChange={(e) => {
                        const isChecked = e.target.checked;
                        const currentSchedules = editProfile.workPeriod.split(', ').filter(s => s);
                        let newSchedules;
                        if (isChecked) {
                          newSchedules = [...currentSchedules, schedule];
                        } else {
                          newSchedules = currentSchedules.filter(s => s !== schedule);
                        }
                        setEditProfile(prev => ({ ...prev, workPeriod: newSchedules.join(', ') }));
                      }}
                      className="rounded border-gray-300 text-[#FF8C00] focus:ring-[#FF8C00]"
                    />
                    <span>{schedule}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className={getLabelClassName('startDate')}>Available Start Date</label>
              <input 
                type="date" 
                name="startDate" 
                value={editProfile.startDate && editProfile.startDate !== 'Jun 09, 2025' ? 
                  new Date(editProfile.startDate).toISOString().split('T')[0] : 
                  new Date().toISOString().split('T')[0]
                }
                onChange={(e) => {
                  const date = new Date(e.target.value);
                  const formattedDate = date.toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  });
                  setEditProfile(prev => ({ ...prev, startDate: formattedDate }));
                }}
                className={getInputClassName('startDate')} 
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>
          </div>
          
          {/* Skills & Experience Section */}
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center">
              <svg className="h-5 w-5 mr-2 text-[#FF8C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              Skills & Experience
            </h3>
          
          {/* Skills Section */}
          <div className="mb-4 sm:mb-6">
            <label className={getLabelClassName('skills')}>Skills</label>
            <div className={`flex flex-wrap gap-2 mb-2 ${isFieldMissing('skills') ? 'animate-shake' : ''}`}>
              {editProfile.skills.map((skill, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                  {skill}
                  <button
                    type="button"
                    onClick={() => {
                      const newSkills = editProfile.skills.filter((_, i) => i !== index);
                      setEditProfile(prev => ({ ...prev, skills: newSkills }));
                    }}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              {isFieldMissing('skills') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-600 animate-pulse">
                  Add at least one skill
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Add a skill (e.g., Customer Service, Food Safety)"
                className={getInputClassName('skills', "flex-1 border rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")}
                list="skills-datalist"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const skill = e.target.value.trim();
                    if (skill && !editProfile.skills.includes(skill)) {
                      setEditProfile(prev => ({ ...prev, skills: [...prev.skills, skill] }));
                      e.target.value = '';
                    }
                  }
                }}
              />
              <datalist id="skills-datalist">
                <option value="Customer Service" />
                <option value="Food Safety" />
                <option value="Cash Handling" />
                <option value="POS Systems" />
                <option value="Communication" />
                <option value="Teamwork" />
                <option value="Time Management" />
                <option value="Problem Solving" />
                <option value="Multitasking" />
                <option value="Food Preparation" />
              </datalist>
            </div>
          </div>
          
          {/* Languages Section */}
          <div className="mb-4 sm:mb-6">
            <label className={getLabelClassName('languages')}>Languages</label>
            <div className={`flex flex-wrap gap-2 mb-2 ${isFieldMissing('languages') ? 'animate-shake' : ''}`}>
              {editProfile.languages.map((language, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                  {language}
                  <button
                    type="button"
                    onClick={() => {
                      const newLanguages = editProfile.languages.filter((_, i) => i !== index);
                      setEditProfile(prev => ({ ...prev, languages: newLanguages }));
                    }}
                    className="ml-2 text-green-600 hover:text-green-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              {isFieldMissing('languages') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-600 animate-pulse">
                  Add at least one language
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Add a language"
                className={getInputClassName('languages', "flex-1 border rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")}
                list="languages-datalist"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const language = e.target.value.trim();
                    if (language && !editProfile.languages.includes(language)) {
                      setEditProfile(prev => ({ ...prev, languages: [...prev.languages, language] }));
                      e.target.value = '';
                    }
                  }
                }}
              />
              <datalist id="languages-datalist">
                <option value="English" />
                <option value="Spanish" />
                <option value="French" />
                <option value="Mandarin" />
                <option value="Hindi" />
                <option value="Arabic" />
                <option value="Portuguese" />
                <option value="Russian" />
                <option value="Japanese" />
                <option value="German" />
              </datalist>
            </div>
          </div>
          
          {/* Desired Jobs Section */}
          <div className="mb-4 sm:mb-6">
            <label className={getLabelClassName('desiredJobs')}>Desired Job Positions</label>
            <div className={`flex flex-wrap gap-2 mb-2 ${isFieldMissing('desiredJobs') ? 'animate-shake' : ''}`}>
              {editProfile.desiredJobs.map((job, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-orange-100 text-orange-800">
                  {job}
                  <button
                    type="button"
                    onClick={() => {
                      const newJobs = editProfile.desiredJobs.filter((_, i) => i !== index);
                      setEditProfile(prev => ({ ...prev, desiredJobs: newJobs }));
                    }}
                    className="ml-2 text-orange-600 hover:text-orange-800"
                  >
                    ×
                  </button>
                </span>
              ))}
              {isFieldMissing('desiredJobs') && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-red-100 text-red-600 animate-pulse">
                  Add at least one desired job
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Add desired job position"
                className={getInputClassName('desiredJobs', "flex-1 border rounded-md px-3 py-2 text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")}
                list="jobs-datalist"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const job = e.target.value.trim();
                    if (job && !editProfile.desiredJobs.includes(job)) {
                      setEditProfile(prev => ({ ...prev, desiredJobs: [...prev.desiredJobs, job] }));
                      e.target.value = '';
                    }
                  }
                }}
              />
              <datalist id="jobs-datalist">
                <option value="Server" />
                <option value="Host/Hostess" />
                <option value="Cashier" />
                <option value="Cook" />
                <option value="Prep Cook" />
                <option value="Bartender" />
                <option value="Barista" />
                <option value="Manager" />
                <option value="Assistant Manager" />
                <option value="Busser" />
                <option value="Kitchen Helper" />
                <option value="Delivery Driver" />
                <option value="Dishwasher" />
                <option value="Food Runner" />
              </datalist>
            </div>
          </div>
          
          {/* About Me Section */}
          <div className="mb-4 sm:mb-6">
            <label className={getLabelClassName('aboutMe')}>About Me</label>
            <textarea 
              name="aboutMe" 
              value={editProfile.aboutMe} 
              onChange={handleInputChange} 
              className={getInputClassName('aboutMe', "w-full border rounded-md px-3 py-2 min-h-[80px] sm:min-h-[100px] text-sm sm:text-base focus:outline-none focus:ring-2 focus:border-transparent")} 
              placeholder="Tell employers about yourself..."
            />
          </div>
          </div>

          {/* Education & Experience Section */}
          <div className="mb-4">
            <h3 className="text-md font-semibold text-gray-800 mb-2 flex items-center">
              <svg className="h-5 w-5 mr-2 text-[#FF8C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253z" />
              </svg>
              Education & Experience
            </h3>

            {/* Education Section */}
            <div className="mb-3 sm:mb-4">
              <label className={getLabelClassName('educations')}>Education</label>
              <div className={`space-y-2 mb-2 ${isFieldMissing('educations') ? 'animate-shake' : ''}`}>
                {editProfile.educations && editProfile.educations.length > 0 ? (
                  editProfile.educations.map((education, index) => (
                    <div key={education.id || index} className="group border-b border-gray-200 py-3 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex flex-1 min-w-0">
                          <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-base font-semibold text-gray-900 leading-tight">
                              {education.school}
                            </div>
                            <div className="text-sm text-gray-700 mt-0.5">
                              {education.degree}{education.major && `, ${education.major}`}
                            </div>
                            <div className="text-sm text-gray-500 mt-0.5">
                              {education.year}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newEducations = editProfile.educations.filter((_, i) => i !== index);
                            setEditProfile(prev => ({ ...prev, educations: newEducations }));
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-400 hover:text-red-600 p-1 ml-2 flex-shrink-0"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  isFieldMissing('educations') && (
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50 text-red-600 text-sm animate-pulse flex items-center">
                      <svg className="h-5 w-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Add your education background to strengthen your profile
                    </div>
                  )
                )}
              </div>
              <button
                type="button"
                className="flex items-center w-full text-[#FF8C00] hover:bg-orange-50 transition-colors duration-200 border-t border-gray-200 py-3"
                onClick={() => setShowEducationModal(true)}
              >
                <div className="w-12 h-12 rounded bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="h-6 w-6 text-[#FF8C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-sm font-medium">Add education</span>
              </button>
            </div>

            {/* Work Experience Section */}
            <div className="mb-3 sm:mb-4">
              <label className={getLabelClassName('experiences')}>Work Experience</label>
              <div className={`space-y-2 mb-2 ${isFieldMissing('experiences') ? 'animate-shake' : ''}`}>
                {editProfile.experiences && editProfile.experiences.length > 0 ? (
                  editProfile.experiences.map((experience, index) => (
                    <div key={experience.id || index} className="group border-b border-gray-200 py-3 hover:bg-gray-50 transition-colors duration-200">
                      <div className="flex items-start justify-between">
                        <div className="flex flex-1 min-w-0">
                          <div className="w-12 h-12 rounded bg-gray-100 flex items-center justify-center mr-3 flex-shrink-0">
                            <svg className="h-6 w-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-base font-semibold text-gray-900 leading-tight">
                                {experience.jobTitle}
                              </span>
                              {experience.isCurrentWork && (
                                <span className="px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded">
                                  Current
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-700 mb-0.5">
                              {experience.businessName}
                            </div>
                            <div className="text-sm text-gray-500 mb-2">
                              {experience.startDate && (() => {
                                let startDate;
                                if (experience.startDate.__time__) {
                                  startDate = new Date(experience.startDate.__time__);
                                } else if (experience.startDate.seconds) {
                                  startDate = new Date(experience.startDate.seconds * 1000);
                                } else {
                                  startDate = new Date(experience.startDate);
                                }
                                return startDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                              })()} - 
                              {experience.isCurrentWork ? ' Present' : (experience.endDate && (() => {
                                let endDate;
                                if (experience.endDate.__time__) {
                                  endDate = new Date(experience.endDate.__time__);
                                } else if (experience.endDate.seconds) {
                                  endDate = new Date(experience.endDate.seconds * 1000);
                                } else {
                                  endDate = new Date(experience.endDate);
                                }
                                return endDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
                              })())}
                            </div>
                            {experience.description && (
                              <div className="text-sm text-gray-700 mb-2">
                                {experience.description}
                              </div>
                            )}
                            {experience.skills && experience.skills.length > 0 && (
                              <div className="flex flex-wrap gap-1">
                                {experience.skills.slice(0, 5).map((skill, skillIndex) => (
                                  <span key={skillIndex} className="inline-block px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full font-medium border">
                                    {skill}
                                  </span>
                                ))}
                                {experience.skills.length > 5 && (
                                  <span className="inline-block px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                                    +{experience.skills.length - 5} more
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newExperiences = editProfile.experiences.filter((_, i) => i !== index);
                            setEditProfile(prev => ({ ...prev, experiences: newExperiences }));
                          }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-400 hover:text-red-600 p-1 ml-2 flex-shrink-0"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  isFieldMissing('experiences') && (
                    <div className="p-4 border border-red-200 rounded-lg bg-red-50 text-red-600 text-sm animate-pulse flex items-center">
                      <svg className="h-5 w-5 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      Add your work experience to showcase your skills
                    </div>
                  )
                )}
              </div>
              <button
                type="button"
                className="flex items-center w-full text-[#FF8C00] hover:bg-orange-50 transition-colors duration-200 border-t border-gray-200 py-3"
                onClick={() => setShowExperienceModal(true)}
              >
                <div className="w-12 h-12 rounded bg-orange-100 flex items-center justify-center mr-3 flex-shrink-0">
                  <svg className="h-6 w-6 text-[#FF8C00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                </div>
                <span className="text-sm font-medium">Add work experience</span>
              </button>
            </div>
          </div>
          
          {/* Messages */}
          {error && (
            <div className="text-red-600 mb-2 text-sm sm:text-base p-3 bg-red-50 border border-red-200 rounded-md">
              {error}
            </div>
          )}
          {success && (
            <div className="text-green-600 mb-2 text-sm sm:text-base p-3 bg-green-50 border border-green-200 rounded-md">
              {success}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button 
              type="submit" 
              className="w-full sm:w-auto bg-[#FF8C00] text-white px-4 sm:px-6 py-2 sm:py-3 rounded-md font-medium hover:bg-[#FF7700] transition-colors text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed" 
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      )}

      {/* Education Modal */}
      {showEducationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Education</h3>
              <button
                type="button"
                onClick={() => setShowEducationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Degree</label>
                <select
                  name="degree"
                  value={educationForm.degree}
                  onChange={handleEducationInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                >
                  <option value="">Select degree...</option>
                  <option value="High School">High School</option>
                  <option value="Associate's Degree">Associate's Degree</option>
                  <option value="Bachelor's Degree">Bachelor's Degree</option>
                  <option value="Master's Degree">Master's Degree</option>
                  <option value="Doctorate">Doctorate</option>
                  <option value="Certificate">Certificate</option>
                </select>
              </div>
              
              <div className="relative suggestion-container">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {educationForm.degree === 'High School' ? 'Program/Track' : 'Major/Field of Study'}
                </label>
                <input
                  type="text"
                  name="major"
                  value={educationForm.major}
                  onChange={handleEducationInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder={educationForm.degree === 'High School' ? 'e.g., College Preparatory, STEM Track, AP Program' : 'e.g., Computer Science, Business Administration'}
                />
                {showMajorSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {majorSuggestions.map((major, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleMajorSuggestionClick(major)}
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 hover:text-[#FF8C00] text-sm"
                      >
                        {major}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="relative suggestion-container">
                <label className="block text-sm font-medium text-gray-700 mb-1">School/Institution</label>
                <input
                  type="text"
                  name="school"
                  value={educationForm.school}
                  onChange={handleEducationInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder={educationForm.degree === 'High School' ? 'e.g., Lincoln High School, Phillips Exeter Academy' : 'e.g., University of California, Harvard University'}
                  required
                />
                {showSchoolSuggestions && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {schoolSuggestions.map((school, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => handleSchoolSuggestionClick(school)}
                        className="w-full text-left px-3 py-2 hover:bg-orange-50 hover:text-[#FF8C00] text-sm"
                      >
                        {school}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Year</label>
                <input
                  type="text"
                  name="year"
                  value={educationForm.year}
                  onChange={handleEducationInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder={educationForm.degree === 'High School' ? 'e.g., 2018, 2019, 2020' : 'e.g., 2020, 2022, 2024'}
                  required
                />
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowEducationModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddEducation}
                className="flex-1 px-4 py-2 text-white bg-[#FF8C00] rounded-lg hover:bg-[#FF7700] transition-colors"
              >
                Add Education
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Experience Modal */}
      {showExperienceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add Work Experience</h3>
              <button
                type="button"
                onClick={() => setShowExperienceModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                <input
                  type="text"
                  name="jobTitle"
                  value={experienceForm.jobTitle}
                  onChange={handleExperienceInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="e.g., Server, Cook, Manager"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company/Business</label>
                <input
                  type="text"
                  name="businessName"
                  value={experienceForm.businessName}
                  onChange={handleExperienceInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  placeholder="e.g., McDonald's, Local Restaurant"
                  required
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={experienceForm.startDate}
                    onChange={handleExperienceInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={experienceForm.endDate}
                    onChange={handleExperienceInputChange}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                    disabled={experienceForm.isCurrentWork}
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isCurrentWork"
                  checked={experienceForm.isCurrentWork}
                  onChange={handleExperienceInputChange}
                  className="h-4 w-4 text-[#FF8C00] border-gray-300 rounded focus:ring-[#FF8C00]"
                />
                <label className="ml-2 text-sm text-gray-700">I currently work here</label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={experienceForm.description}
                  onChange={handleExperienceInputChange}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                  rows="3"
                  placeholder="Describe your responsibilities and achievements..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Skills Used</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {experienceForm.skills.map((skill, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">
                      {skill}
                      <button
                        type="button"
                        onClick={() => handleRemoveSkillFromExperience(skill)}
                        className="ml-1 text-orange-600 hover:text-orange-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={currentSkill}
                    onChange={(e) => setCurrentSkill(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#FF8C00]"
                    placeholder="Add a skill..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSkillToExperience();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddSkillToExperience}
                    className="px-3 py-2 bg-[#FF8C00] text-white rounded-md hover:bg-[#FF7700] transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <button
                type="button"
                onClick={() => setShowExperienceModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddExperience}
                className="flex-1 px-4 py-2 text-white bg-[#FF8C00] rounded-lg hover:bg-[#FF7700] transition-colors"
              >
                Add Experience
              </button>
            </div>
          </div>
        </div>
      )}
      

    </div>
  );
};

export default ProfileDashboard; 